import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ImageUpload from "@/components/ImageUpload";
import { FileText, Clock, CheckCircle2, XCircle, RefreshCw, HelpCircle } from "lucide-react";

const DOC_LABELS = {
  id_doc: "Pièce d'identité du représentant",
  rccm: "Registre du commerce (RCCM ou équivalent)",
  activity_proof: "Justificatif d'activité",
  address_proof: "Justificatif d'adresse",
  vehicles_docs: "Documents véhicules (carte grise)",
  insurance: "Attestation d'assurance",
  rib: "RIB / Coordonnées bancaires",
};

const STATUS_META = {
  not_submitted: { label: "Non fourni", cls: "bg-gray-100 text-gray-600", Icon: HelpCircle },
  submitted: { label: "Reçu", cls: "bg-blue-100 text-blue-700", Icon: Clock },
  in_review: { label: "En vérification", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  validated: { label: "Validé", cls: "bg-[#dcfce7] text-[#0e7a3f]", Icon: CheckCircle2 },
  rejected: { label: "Refusé", cls: "bg-red-100 text-red-700", Icon: XCircle },
  replacement_needed: { label: "À remplacer", cls: "bg-orange-100 text-orange-700", Icon: RefreshCw },
};

const PS_META = {
  pending: { label: "En attente de validation MOBIKAR", cls: "bg-amber-100 text-amber-800" },
  in_review: { label: "Dossier en vérification", cls: "bg-blue-100 text-blue-700" },
  docs_missing: { label: "Documents manquants ou à remplacer", cls: "bg-orange-100 text-orange-700" },
  contract_pending: { label: "Contrat prêt — à consulter dans Mon contrat", cls: "bg-purple-100 text-purple-700" },
  contract_signed: { label: "Contrat accepté — validation finale MOBIKAR en cours", cls: "bg-[#0e7a3f]/10 text-[#0e7a3f]" },
  validated: { label: "Partenariat validé — vous êtes actif", cls: "bg-[#dcfce7] text-[#0e7a3f]" },
  refused: { label: "Partenariat refusé", cls: "bg-red-100 text-red-700" },
  deactivated: { label: "Partenariat désactivé", cls: "bg-gray-200 text-gray-700" },
};

export default function AgencyDossier() {
  const [agency, setAgency] = useState(null);
  const [uploading, setUploading] = useState(null);
  const load = () => api.get("/agency/me").then((r) => setAgency(r.data)).catch((e) => toast.error(extractError(e)));
  useEffect(() => { load(); }, []);

  const upload = async (docType, url) => {
    if (!url) return;
    setUploading(docType);
    try {
      await api.post("/agency/documents", { doc_type: docType, url });
      toast.success("Document envoyé — en attente de vérification");
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploading(null); }
  };

  if (!agency) return <div className="text-center text-gray-400 py-24">Chargement de votre dossier…</div>;
  const ps = PS_META[agency.partnership_status] || PS_META.pending;
  const required = agency.required_documents || [];
  const docs = agency.partnership_documents || {};

  return (
    <div data-testid="agency-dossier">
      <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Mon dossier</h1>
      <p className="text-sm text-gray-500 mt-1">Suivi de votre demande de partenariat et gestion de vos documents.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Statut du partenariat</div>
            <div className="mt-3 flex items-center gap-3">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${ps.cls}`} data-testid="dossier-status-badge">{ps.label}</span>
            </div>
            {agency.partnership_notes && <div className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 italic">Note MOBIKAR : {agency.partnership_notes}</div>}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-[#0e7a3f]" />
              <div className="font-display text-lg font-black text-[#0a1e3f]">Documents demandés</div>
            </div>
            {required.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center">Aucun document demandé pour l'instant. MOBIKAR vous informera si de nouveaux documents sont requis.</div>
            ) : (
              <div className="space-y-4">
                {required.map((docType) => {
                  const info = docs[docType] || {};
                  const status = info.status || "not_submitted";
                  const meta = STATUS_META[status];
                  const Icon = meta.Icon;
                  const url = info.url ? (info.url.startsWith("http") ? info.url : `${API_BASE.replace(/\/api$/, "")}${info.url}`) : "";
                  return (
                    <div key={docType} className="border border-gray-100 rounded-xl p-4" data-testid={`doc-row-${docType}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="font-semibold text-[#0a1e3f]">{DOC_LABELS[docType] || docType}</div>
                          {info.uploaded_at && <div className="text-xs text-gray-500 mt-0.5">Envoyé le {new Date(info.uploaded_at).toLocaleString("fr-FR")}</div>}
                        </div>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 shrink-0 ${meta.cls}`}>
                          <Icon className="h-3 w-3" /> {meta.label}
                        </span>
                      </div>
                      {info.note && <div className="text-xs text-orange-800 bg-orange-50 rounded-lg p-2 mb-3">Note MOBIKAR : {info.note}</div>}
                      {(status === "not_submitted" || status === "rejected" || status === "replacement_needed") && (
                        <ImageUpload value="" onChange={(url) => upload(docType, url)} label={uploading === docType ? "Envoi…" : "Téléverser"} testId={`upload-${docType}`} />
                      )}
                      {url && ["submitted", "in_review", "validated"].includes(status) && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#0a1e3f] hover:text-[#0e7a3f] underline">
                          Voir le document
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mbk-card-shadow">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Informations agence</div>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Nom :</span> <b className="text-[#0a1e3f]">{agency.name}</b></div>
              <div><span className="text-gray-500">Représentant :</span> {agency.representative_first_name} {agency.representative_last_name}</div>
              <div><span className="text-gray-500">Pays / Ville :</span> {agency.country}, {agency.city}</div>
              <div><span className="text-gray-500">RCCM :</span> {agency.rccm_number || "—"}</div>
              <div><span className="text-gray-500">Forme juridique :</span> {agency.legal_form || "—"}</div>
              <div><span className="text-gray-500">Commission MOBIKAR :</span> <b className="text-[#0e7a3f]">{agency.commission_percent ?? "—"} %</b></div>
            </div>
          </div>
          <div className="bg-[#0a1e3f]/5 rounded-2xl p-5 text-sm">
            <div className="font-bold text-[#0a1e3f] mb-2">Processus de validation</div>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 text-xs">
              <li>Inscription reçue ✓</li>
              <li>Vérification des documents par MOBIKAR</li>
              <li>Génération du contrat de partenariat</li>
              <li>Acceptation électronique du contrat par vos soins</li>
              <li>Validation finale MOBIKAR → activation</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
